import { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { githubApi, GitHubConnection, GitHubOrg } from "@/lib/api/github";
import { openOAuthPopup } from "@/lib/connector-oauth";
import toast from "react-hot-toast";
import {
  Github,
  Loader2,
  Plus,
  Building,
  User,
  ExternalLink,
  AlertCircle,
  Download,
} from "lucide-react";

interface GitHubModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: string;
  projectSlug: string;
  existingConnection?: {
    connectionId: string;
    repoUrl: string;
    repoOwner: string;
    repoName: string;
    defaultBranch: string;
    syncEnabled: boolean;
  };
}

type Step = "select-connection" | "configure" | "confirm";

export default function GitHubModal({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  projectSlug,
  existingConnection,
}: GitHubModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  // True while the OAuth popup is open AND while we're refreshing the
  // connections list afterwards. Embed contexts (OptiSigns iframe) can delay
  // the popup→parent message by several seconds because of storage
  // partitioning; without an explicit loading state the modal sits on its
  // pre-OAuth view, making the new account appear to load 5s late.
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [step, setStep] = useState<Step>("select-connection");

  // Connection state
  const [connections, setConnections] = useState<GitHubConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>("");
  const [selectedConnection, setSelectedConnection] =
    useState<GitHubConnection | null>(null);

  // Config state
  const [organizations, setOrganizations] = useState<GitHubOrg[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>("");
  const [repoName, setRepoName] = useState(projectSlug);
  const [branch, setBranch] = useState("main");
  const [isPrivate, setIsPrivate] = useState(true);
  const [createRepo, setCreateRepo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load connections on mount
  useEffect(() => {
    if (isOpen) {
      loadConnections();
      setError(null); // Clear any previous errors
      if (existingConnection) {
        setSelectedConnectionId(existingConnection.connectionId);
        setSelectedOwner(existingConnection.repoOwner);
        setRepoName(existingConnection.repoName);
        setBranch(existingConnection.defaultBranch);
        setCreateRepo(false);
      }
    }
  }, [isOpen, existingConnection]);

  // Load orgs when connection changes
  useEffect(() => {
    if (selectedConnectionId) {
      const conn = connections.find((c) => c.id === selectedConnectionId);
      setSelectedConnection(conn || null);
      if (conn) {
        loadOrganizations(selectedConnectionId);
        // Default to personal account
        setSelectedOwner(conn.metadata?.login || "");
      }
    }
  }, [selectedConnectionId, connections]);

  const loadConnections = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await githubApi.listConnections(token);
      setConnections(data);
      if (data.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(data[0].id);
      }
    } catch (error) {
      console.error("Failed to load GitHub connections", error);
    }
  };

  const loadOrganizations = async (connectionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const orgs = await githubApi.listOrganizations(token, connectionId);
      setOrganizations(orgs);
    } catch (error) {
      console.error("Failed to load organizations", error);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      const { authUrl } = await githubApi.initiateOAuth(
        token,
        undefined,
        undefined,
        '/connections/oauth/callback',
      );
      const prevIds = new Set(connections.map((c) => c.id));
      setConnectingOAuth(true);
      try {
        const result = await openOAuthPopup(authUrl, {
          fallbackCheck: async () => {
            const t = await getIdToken();
            if (!t) return null;
            const data = await githubApi.listConnections(t);
            const newConn = data.find((c) => !prevIds.has(c.id));
            return newConn ? { success: true, connectionId: newConn.id } : null;
          },
        });
        if (result.success) {
          await loadConnections();
        }
      } finally {
        setConnectingOAuth(false);
      }
    } catch (error) {
      console.error("Failed to initiate GitHub OAuth", error);
      toast.error("Failed to connect GitHub");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedConnectionId || !selectedOwner || !repoName) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Please sign in to continue");
        return;
      }
      await githubApi.connectProject(token, projectId, {
        connectionId: selectedConnectionId,
        owner: selectedOwner,
        repoName,
        branch,
        isPrivate,
        createRepo,
      });
      toast.success("Project connected to GitHub!");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Failed to connect project to GitHub", error);
      setError(error.response?.data?.message || "Failed to connect to GitHub");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      await githubApi.disconnectProject(token, projectId);
      toast.success("Disconnected from GitHub");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Failed to disconnect from GitHub", error);
      toast.error("Failed to disconnect");
    } finally {
      setLoading(false);
    }
  };

  const handlePull = async () => {
    try {
      setPulling(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      const result = await githubApi.pullFromGithub(token, projectId);
      if (result.success) {
        toast.success("Pulled latest changes from GitHub");
      } else {
        toast.error(result.error || "Failed to pull from GitHub");
      }
    } catch (error: any) {
      console.error("Failed to pull from GitHub", error);
      toast.error(
        error.response?.data?.message || "Failed to pull from GitHub",
      );
    } finally {
      setPulling(false);
    }
  };

  const renderSelectConnection = () => (
    <div className="space-y-4">
      {connections.length === 0 ? (
        <div className="text-center py-8">
          <Github className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-6">
            No GitHub accounts connected
          </p>

          <Button onClick={handleConnectGitHub} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Github className="h-4 w-4 mr-2" />
            )}
            Connect GitHub Account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GitHub Account</Label>
            <Select
              value={selectedConnectionId}
              onValueChange={setSelectedConnectionId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select GitHub account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={conn.metadata?.avatar_url} />
                        <AvatarFallback>
                          {conn.metadata?.login?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>{conn.metadata?.login || conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleConnectGitHub}
            disabled={loading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add another account
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep("configure")}
              disabled={!selectedConnectionId}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderConfigure = () => (
    <div className="space-y-4">
      {/* Owner selection */}
      <div className="space-y-2">
        <Label>Repository Owner</Label>
        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger>
            <SelectValue placeholder="Select owner" />
          </SelectTrigger>
          <SelectContent>
            {/* Personal account */}
            {selectedConnection?.metadata?.login && (
              <SelectItem value={selectedConnection.metadata.login}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedConnection.metadata.login}</span>
                  <span className="text-muted-foreground text-xs">
                    (Personal)
                  </span>
                </div>
              </SelectItem>
            )}
            {/* Organizations */}
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.login}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={org.avatar_url} />
                    <AvatarFallback>
                      <Building className="h-3 w-3" />
                    </AvatarFallback>
                  </Avatar>
                  <span>{org.login}</span>
                </div>
              </SelectItem>
            ))}
            {/* Add another GitHub account */}
            <SelectSeparator />
            <div
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent focus:bg-accent"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleConnectGitHub();
              }}
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Plus className="h-4 w-4" />
                <span>Add GitHub Account</span>
              </div>
            </div>
          </SelectContent>
        </Select>
      </div>

      {/* Repository name */}
      <div className="space-y-2">
        <Label>Repository Name</Label>
        <Input
          value={repoName}
          onChange={(e) => setRepoName(e.target.value)}
          placeholder="my-project"
        />
      </div>

      {/* Branch */}
      <div className="space-y-2">
        <Label>Branch</Label>
        <Input
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          placeholder="main"
        />
      </div>

      {/* Create new repo toggle */}
      {!existingConnection && (
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Create new repository</Label>
            <p className="text-sm text-muted-foreground">
              Create a new repo or push to existing
            </p>
          </div>
          <Switch checked={createRepo} onCheckedChange={setCreateRepo} />
        </div>
      )}

      {/* Private repo toggle */}
      {createRepo && (
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Private repository</Label>
            <p className="text-sm text-muted-foreground">
              Only visible to you and collaborators
            </p>
          </div>
          <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
        </div>
      )}

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep("select-connection")}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setStep("confirm");
              setError(null);
            }}
            disabled={!selectedOwner || !repoName}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );

  const renderConfirm = () => (
    <div className="space-y-4">
      <div className="bg-muted p-4 rounded-lg space-y-2">
        <p className="text-sm">
          Your code will sync to GitHub ({selectedOwner}).
        </p>
        <p className="text-sm text-muted-foreground">
          You can disconnect anytime in Project Settings.
        </p>
      </div>

      <div className="bg-card border rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Github className="h-4 w-4" />
          <span className="font-medium">
            {selectedOwner}/{repoName}
          </span>
        </div>
        <div className="text-sm text-muted-foreground">Branch: {branch}</div>
        {createRepo && (
          <div className="text-sm text-muted-foreground">
            Visibility: {isPrivate ? "Private" : "Public"}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setStep("configure");
            setError(null);
          }}
        >
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="destructive"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Transfer anyway
          </Button>
        </div>
      </div>
    </div>
  );

  const renderExistingConnection = () => (
    <div className="space-y-4">
      <div className="bg-card border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            <span className="font-medium">Connected to GitHub</span>
          </div>
          <a
            href={existingConnection?.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            View on GitHub
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="text-sm text-muted-foreground">
          {existingConnection?.repoOwner}/{existingConnection?.repoName}
        </div>
        <div className="text-sm text-muted-foreground">
          Branch: {existingConnection?.defaultBranch}
        </div>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button
          variant="outline"
          onClick={handleDisconnect}
          disabled={loading || pulling}
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Disconnect
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePull}
            disabled={loading || pulling}
          >
            {pulling ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Pull
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            GitHub
          </DialogTitle>
          <DialogDescription>
            {existingConnection
              ? "Manage your GitHub connection"
              : "Sync your project 2-way with GitHub to collaborate at source."}
          </DialogDescription>
        </DialogHeader>

        {connectingOAuth ? (
          <div className="flex flex-col items-center justify-center text-center py-10 space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Connecting to GitHub…</p>
              <p className="text-xs text-muted-foreground mt-1">
                Finishing authentication. This will only take a moment.
              </p>
            </div>
          </div>
        ) : existingConnection ? (
          renderExistingConnection()
        ) : (
          <>
            {step === "select-connection" && renderSelectConnection()}
            {step === "configure" && renderConfigure()}
            {step === "confirm" && renderConfirm()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
